import { useContext } from 'react'
import { AuthContext } from './authContext'
import { supabase } from '../lib/supabase'
import { isAdminUser } from '../lib/adminAuth'
import { extractFunctionError } from '../lib/functionsError'
import { loadAppleSdk } from '../lib/appleAuth'

export default function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) return { ok: false, message: error.message }
    return { ok: true, data }
  }

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/get-started`,
      },
    })
    if (error) return { ok: false, message: error.message }
    return { ok: true, data }
  }

  const signInWithProvider = async (provider) => {
    // Land OAuth users on /get-started like the email flow, so brand-new
    // accounts still complete their business profile + phone verification.
    // (/get-started redirects already-onboarded users straight to /dashboard.)
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/get-started`,
      },
    })
    if (error) return { ok: false, message: error.message }
    return { ok: true, data }
  }

  // Apple sign-in uses Sign in with Apple JS (a popup that returns an identity
  // token) rather than the Supabase redirect, so we also get the user's name on
  // first authorization. The token is exchanged for a session via signInWithIdToken.
  // https://supabase.com/docs/guides/auth/social-login/auth-apple
  const signInWithApple = async () => {
    const clientId = import.meta.env.VITE_APPLE_SERVICES_ID
    if (!clientId) {
      return { ok: false, message: 'Apple sign-in is not configured.' }
    }
    try {
      const AppleID = await loadAppleSdk()
      // The same nonce is passed to Apple and to Supabase for verification.
      const nonce = crypto.randomUUID()
      AppleID.auth.init({
        clientId,
        scope: 'name email',
        redirectURI: `${window.location.origin}/login`,
        usePopup: true,
        nonce,
      })

      const response = await AppleID.auth.signIn()
      const idToken = response?.authorization?.id_token
      if (!idToken) {
        return { ok: false, message: 'Apple did not return an identity token.' }
      }

      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: idToken,
        nonce,
      })
      if (error) return { ok: false, message: error.message }

      // Apple only sends the name on the very first authorization — persist it.
      const name = response?.user?.name
      if (name && (name.firstName || name.lastName)) {
        const fullName = [name.firstName, name.lastName].filter(Boolean).join(' ')
        await supabase.auth.updateUser({
          data: {
            full_name: fullName,
            given_name: name.firstName,
            family_name: name.lastName,
          },
        })
      }

      return { ok: true, data }
    } catch (err) {
      // User dismissed the Apple popup — not an error worth surfacing.
      if (
        err?.error === 'popup_closed_by_user' ||
        err?.error === 'user_cancelled_authorize' ||
        err?.error === 'user_trigger_new_signin_flow'
      ) {
        return { ok: false, canceled: true }
      }
      return { ok: false, message: err?.message || 'Apple sign-in failed.' }
    }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) return { ok: false, message: error.message }
    return { ok: true }
  }

  const sendPhoneOtp = async (phone) => {
    const { error } = await supabase.functions.invoke('send-phone-otp', {
      body: { phone },
    })
    if (error) {
      return { ok: false, message: await extractFunctionError(error, 'Could not send verification code.') }
    }
    return { ok: true }
  }

  const verifyPhoneOtp = async (phone, code) => {
    const { error } = await supabase.functions.invoke('verify-phone-otp', {
      body: { phone, code },
    })
    if (error) {
      return { ok: false, message: await extractFunctionError(error, 'Invalid or expired code.') }
    }
    return { ok: true }
  }

  const sendWorkEmailOtp = async (email) => {
    const { error } = await supabase.functions.invoke('send-work-email-otp', {
      body: { email },
    })
    if (error) {
      return { ok: false, message: await extractFunctionError(error, 'Could not send verification code.') }
    }
    return { ok: true }
  }

  const verifyWorkEmailOtp = async (email, code) => {
    const { error } = await supabase.functions.invoke('verify-work-email-otp', {
      body: { email, code },
    })
    if (error) {
      return { ok: false, message: await extractFunctionError(error, 'Invalid or expired code.') }
    }
    return { ok: true }
  }

  return {
    session: ctx.session,
    user: ctx.user,
    isAuthed: ctx.isAuthed,
    isAdmin: isAdminUser(ctx.user),
    loading: ctx.loading,
    signIn,
    signUp,
    signInWithProvider,
    signInWithApple,
    signOut,
    resetPassword,
    sendPhoneOtp,
    verifyPhoneOtp,
    sendWorkEmailOtp,
    verifyWorkEmailOtp,
  }
}
